/**
 * WCAG 2.1 contrast. The measure every identity colour is proved against.
 *
 * ⚠️ THIS IS NOT `luma()` IN app/lib/theme.ts. That one is Rec. 601 luma on a
 * 0–255 scale, used for a single heuristic threshold (`MAX_IDENTITY_LUMA = 110`)
 * deciding whether a tenant's colour is too pale to be the marketing site's
 * identity slot. It cannot produce a contrast RATIO — it skips the sRGB
 * linearisation entirely — so it can tell you "this looks light" and can never
 * tell you "text on this is readable". Both exist on purpose; do not merge them
 * and do not extend `luma()` to do this job.
 */

import { linearRgb } from './oklch';

/** WCAG 2.1 relative luminance, 0 (black) – 1 (white). */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = linearRgb(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG 2.1 contrast ratio, 1 (identical) – 21 (black on white).
 * Order-independent.
 */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** WCAG AA for body text, and for any label sitting on a brand ground. */
export const AA_TEXT = 4.5;

/**
 * WCAG AA for large text (≥24px, or ≥18.66px bold) — and the 1.4.11 threshold
 * for non-text: borders, focus rings, and a filled control's own boundary
 * against the page.
 */
export const AA_LARGE = 3;

/**
 * WCAG AAA. Used for one thing: `--ink-strong` on the identity wash. A washed
 * table row carries dense tabular numerals (`--num-feat`), which are harder to
 * read than prose at the same ratio, and it is the surface a dispatcher stares
 * at all day.
 */
export const AAA_TEXT = 7;

/**
 * Of two candidate inks, the one that reads better on `ground`.
 *
 * Deliberately returns one of the two colours it was handed rather than
 * inventing a third. That constraint is the structural reason a tenant's hex can
 * never become text: identity owns grounds, the frozen ink ramp owns every
 * character on screen.
 */
export function bestInkOn(ground: string, inkDark: string, inkLight: string): string {
  return contrastRatio(ground, inkLight) >= contrastRatio(ground, inkDark) ? inkLight : inkDark;
}
